import React from "react"
import { Box, Center, VStack } from "@chakra-ui/layout"
import { Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/table"
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons"
import { useFilters, useGlobalFilter, useSortBy, useTable } from "react-table"
import DefaultColumnFilter from "./tablefilters/DefaultColumnFilter"
import GlobalFilter from "./tablefilters/GlobalFilter"

type ChangeHealthcareResponseTableProps = {
  response: string
}

function getBenefitLinesFromResponse(response) {
  if (response.benefitsInformation === undefined) {
    return null
  }

  return response.benefitsInformation.map((b) => {
    return {
      code: b.code,
      coverageLevel: b.coverageLevel,
      serviceTypeCodes: b.serviceTypeCodes?.join(", "),
      serviceTypeDescription: b.serviceTypes?.join(", "),
      timeQualifier: b.timeQualifier,
      benefitAmount: b.benefitAmount,
      inPlanNetwork: b.inPlanNetworkIndicator
    }
  })
}

export default function ChangeHealthcareResponseTable({ response }: ChangeHealthcareResponseTableProps): JSX.Element {
  const columns = React.useMemo(
    () => [
      {
        Header: "Benefit Lines in Response",
        columns: [
          {
            Header: "Code",
            accessor: "code"
          },
          {
            Header: "Service Type Codes",
            accessor: "serviceTypeCodes"
          },
          {
            Header: "In Plan Network",
            accessor: "inPlanNetwork"
          },
          {
            Header: "Coverage Level",
            accessor: "coverageLevel"
          },
          {
            Header: "Service Type Description",
            accessor: "serviceTypeDescription"
          },
          {
            Header: "Time Qualifier",
            accessor: "timeQualifier"
          },
          {
            Header: "Benefit Amount",
            accessor: "benefitAmount"
          }
        ]
      }
    ],
    []
  )

  const benefitLines = getBenefitLinesFromResponse(response)

  if (benefitLines === null)
    return <Center>Unable to parse benefit lines from the response. See JSON for more information.</Center>

  return (
    <VStack>
      <Box borderWidth="1px" borderRadius="lg" p={3}>
        <Center mb={2}>
          <strong>Common filters</strong>
        </Center>
        <p>
          <strong>Code:</strong>&emsp;C (deductibles)
        </p>
        <p>
          <strong>Service Type Code:</strong>&emsp;30 (most common) or 1
        </p>
        <p>
          <strong>In Plan Network:</strong>&emsp;Yes or Not Applicable
        </p>
      </Box>
      <ChangeHealthResponseTableChild columns={columns} data={benefitLines} />
    </VStack>
  )
}

function ChangeHealthResponseTableChild({ columns, data }) {
  const filterTypes = React.useMemo(
    () => ({
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id]
          return rowValue !== undefined
            ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
            : true
        })
      }
    }),
    []
  )

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter
    }),
    []
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    visibleColumns,
    preGlobalFilteredRows,
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      initialState: {
        sortBy: [
          {
            id: "benefitAmount",
            desc: true
          }
        ]
      }
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  )

  return (
    <>
      <Table {...getTableProps()} size="sm">
        <Thead>
          {headerGroups.map((headerGroup, i) => (
            <Tr key={i} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, j) => (
                <Th key={j}>
                  <div {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render("Header")}
                    <span>
                      {!column.isSorted ? (
                        ""
                      ) : column.isSortedDesc ? (
                        <ChevronDownIcon boxSize={6} />
                      ) : (
                        <ChevronUpIcon boxSize={6} />
                      )}
                    </span>
                  </div>
                  {/* Render the columns filter UI */}
                  <Box marginTop={2}>{column.canFilter ? column.render("Filter") : null}</Box>
                </Th>
              ))}
            </Tr>
          ))}
          <Tr>
            <Th
              colSpan={visibleColumns.length}
              style={{
                textAlign: "left"
              }}
            >
              <GlobalFilter
                preGlobalFilteredRows={preGlobalFilteredRows}
                globalFilter={state.globalFilter}
                setGlobalFilter={setGlobalFilter}
              />
            </Th>
          </Tr>
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row)
            return (
              <Tr key={i} {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <Td key={i} {...cell.getCellProps()}>
                      {cell.render("Cell")}
                    </Td>
                  )
                })}
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </>
  )
}
